import { useState, useEffect, useCallback } from "react";
import { searchService } from "../services/searchService";
import companiesData from "../assets/response.json";

export const useSearch = () => {
  const [searchType, setSearchType] = useState("managers");
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  const [remainingCompanies, setRemainingCompanies] = useState([]);
  const [currentQuery, setCurrentQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Load companies data
  useEffect(() => {
    setAllCompanies(companiesData);
  }, []);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch suggestions when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      fetchSuggestions(debouncedSearchTerm);
    } else {
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchSuggestions = useCallback(
    async (query, append = false) => {
      if (!query.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        if (searchType === "company") {
          const result = searchService.searchCompanies(
            query,
            allCompanies,
            append,
            remainingCompanies
          );

          if (!append) {
            setCurrentQuery(query);
            setSuggestions(
              append ? [...suggestions, ...result.companies] : result.companies
            );
            setRemainingCompanies(result.remainingCompanies);
          } else {
            setSuggestions(
              append ? [...suggestions, ...result.companies] : result.companies
            );
            setRemainingCompanies(result.remainingCompanies);
          }
        } else {
          const result = await searchService.searchManagers(
            query,
            append ? lastKey : null
          );

          if (!append) {
            setLastKey(null);
          }

          setSuggestions(
            append ? [...suggestions, ...result.managers] : result.managers
          );
          setLastKey(result.lastKey);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
      }
    },
    [searchType, lastKey, suggestions, allCompanies, remainingCompanies]
  );

  const handleDropdownChange = (type) => {
    setSearchType(type);
    setIsDropdownOpen(false);
    setSuggestions([]);
    setSearchTerm("");
    setLastKey(null);
    setRemainingCompanies([]);
  };

  const handleViewMore = () => {
    if (searchType === "company") {
      fetchSuggestions(currentQuery, true);
    } else if (searchTerm.trim() && lastKey) {
      fetchSuggestions(searchTerm, true);
    }
  };

  const clearSuggestions = () => {
    setSuggestions([]);
    setSearchTerm("");
  };

  return {
    searchType,
    searchTerm,
    setSearchTerm,
    suggestions,
    isDropdownOpen,
    setIsDropdownOpen,
    lastKey,
    remainingCompanies,
    handleDropdownChange,
    handleViewMore,
    clearSuggestions,
    fetchSuggestions,
  };
};
